import { gql } from "@apollo/client";

import { productSearch } from "../../../modules/api";

const RESOLVE = gql(`
    query ResolveProductIdentifiers($brandID: ID!, $values: [String!]!) {
        resolveProductIdentifiers(brandID: $brandID, values: $values) {
            value
            productID
        }
    }
`);

interface ProductIdentifierMatch {
    value: string;
    productID: string;
}

export interface ResolveResult {
    // manufacturer identifier value -> product UUID
    idByValue: Map<string, string>;
    // values that matched >1 different product (ambiguous, left unresolved)
    conflicts: Set<string>;
}

/**
 * Resolve manufacturer identifier values (column A) to product UUIDs within a
 * brand. The product-search service matches each value against every identifier
 * parameter (EAN/GTIN/Code/Article number) in one query. A value that resolves
 * to two different products is treated as a conflict and left unresolved.
 */
export async function resolveProductIds(
    values: string[],
    brandID: string,
): Promise<ResolveResult> {
    const unique = [...new Set(values.filter(Boolean))];
    const idByValue = new Map<string, string>();
    const conflicts = new Set<string>();

    if (!unique.length) return { idByValue, conflicts };

    const { data } = await productSearch.query<{
        resolveProductIdentifiers: ProductIdentifierMatch[];
    }>({
        query: RESOLVE,
        fetchPolicy: "no-cache",
        variables: { brandID, values: unique },
    });

    for (const { value, productID } of data.resolveProductIdentifiers) {
        if (conflicts.has(value)) continue;

        const existing = idByValue.get(value);
        if (existing && existing !== productID) {
            conflicts.add(value);
            idByValue.delete(value);
        } else {
            idByValue.set(value, productID);
        }
    }

    return { idByValue, conflicts };
}
